import React from 'react'
import { bindActionCreators } from 'redux'
import { useAppDispatch, useAppSelector } from 'reduxTyped'

import * as builderStateActions from 'common/builder/actions/builderStateActions'
import * as contentActions from 'common/builder/actions/contentActions'
import { SelectionIds } from 'common/builder/actions/interfaces/selections'
import { openNode } from 'common/builder/actions/openNode'
import { setSelection, toggleSelection, mergeSelection } from 'common/builder/actions/selections'
import { FlowChartStateContextProvider } from 'common/builder/components/chart/FlowChart/contexts/FlowChartStateContext'
import FlowChartViewer from 'common/builder/components/chart/FlowChart/FlowChartViewer'
import { useFlowChartDataPreloader } from 'common/builder/components/chart/FlowChart/hooks/useFlowChartDataPreloader'
import { useFlowChartNodes } from 'common/builder/components/chart/FlowChart/hooks/useFlowChartNodes'
import { useFlowChartServices } from 'common/builder/components/chart/FlowChart/hooks/useFlowChartServices'
import { useFlowChartTriggers } from 'common/builder/components/chart/FlowChart/hooks/useFlowChartTriggers'
import { useGetViewerFlowChartState } from 'common/builder/controllers/chart/FlowChartViewer/useGetViewerFlowChartState'
import { CommonFlowChartControllerProps } from 'common/builder/controllers/types/CommonFlowChartControllerProps'
import SegmentViewerModal from 'common/builder/controllers/viewer/SegmentViewerModal'
import builderSelectors from 'common/builder/selectors/builder'
import * as entitySelectors from 'common/builder/selectors/builder/entitySelectors'
import * as nextStepSelectors from 'common/builder/selectors/builder/nextStepSelectors'
import { getCurrentAccountID } from 'common/core/selectors/appSelectors'
import * as flowSelectors from 'common/flow/selectors/flowSelectors'

interface FlowChartViewerControllerProps extends CommonFlowChartControllerProps {
  player?: boolean
  onModeChange(): void
  disallowEditMode?: boolean
  isPostFlowViewer: boolean
}

const FlowChartViewerController = (props: FlowChartViewerControllerProps) => {
  const {
    flow,
    pageId,
    blocks,
    buttons,
    coordinates,
    rootId,
    selected,
    activeId,
    nodes,
    transform,
    builderState,
    withTransform,
    isTargetAllowed,
  } = useAppSelector((state) => {
    const { builderId } = props
    const pageId = getCurrentAccountID(state)
    const builderState = builderSelectors.state.getById(state, builderId) || {}
    const nodes = entitySelectors.getNodesMap(state, builderId)
    const flow = flowSelectors.getById(state, props.flowId)

    const sortedTriggers = flowSelectors.getSortedFlowTriggers(state, props.flowId)

    const blocks = entitySelectors.getBlocksMap(state, builderId)
    const buttons = entitySelectors.getButtonsMap(state, builderId)

    const rootId = builderState.rootId || null
    const selected = builderSelectors.state.getSelectionMap(state, builderId)
    const activeId = builderSelectors.state.getCurrentNodeId(state, builderId)

    const coordinates = builderState.coordinates
    const transform = builderState.transform

    const isTargetAllowed = (entityId: string) =>
      nextStepSelectors.isTargetAllowed(state, builderId, entityId)

    return {
      flow,
      pageId,
      blocks,
      buttons,
      coordinates,
      rootId,
      selected,
      activeId,
      nodes,
      sortedTriggers,
      transform,
      builderState,
      withTransform: Object.keys(transform).length > 0,
      isTargetAllowed,
    }
  })

  const { builderId } = props
  const dispatch = useAppDispatch()
  let dispatchActions = {}

  if (!props.player) {
    dispatchActions = {
      contentActions: bindActionCreators(contentActions, dispatch),
      builderStateActions: bindActionCreators(builderStateActions, dispatch),

      selectSegment: function selectSegment(segment: unknown) {
        const { builderId } = props

        dispatch(builderStateActions.selectSegment(builderId, segment))
      },

      setSelection: (ids: SelectionIds) => {
        const { builderId } = props
        dispatch(setSelection(builderId, ids))
      },

      mergeSelection: (ids: SelectionIds) => {
        const { builderId } = props
        dispatch(mergeSelection(builderId, ids))
      },

      toggleSelection: (ids: SelectionIds) => {
        const { builderId } = props
        dispatch(toggleSelection(builderId, ids))
      },

      openNode: (nodeId: string, options: UnsafeAnyObject) => {
        const { builderId } = props
        dispatch(openNode(builderId, nodeId, options))
      },
    }
  }

  const triggers = useFlowChartTriggers({
    flowId: props.flowId,
    externalTriggers: [],
  })

  const services = useFlowChartServices(builderId, flow?.id)
  useFlowChartDataPreloader({ builderId })

  const newNodes = useFlowChartNodes({
    builderId,
    flowId: props.flowId,
    flowChartTriggers: triggers,
  })

  const flowChartState = useGetViewerFlowChartState({
    flow,
    isPlayer: props.player,
    selectedTriggerId: builderState.selectedTrigger,
    selectedNodesMap: builderState.selectedNodes,
  })

  return (
    <FlowChartStateContextProvider value={flowChartState}>
      <div>
        <FlowChartViewer
          {...props}
          flow={flow}
          pageId={pageId}
          blocks={blocks}
          buttons={buttons}
          coordinates={coordinates}
          rootId={rootId}
          selected={selected}
          activeId={activeId}
          nodes={nodes}
          transform={transform}
          builderState={builderState}
          withTransform={withTransform}
          isTargetAllowed={isTargetAllowed}
          triggers={triggers}
          newNodes={newNodes}
          services={services}
          {...dispatchActions}
        />
        {!props.player && <SegmentViewerModal builderId={props.builderId} />}
      </div>
    </FlowChartStateContextProvider>
  )
}

export default FlowChartViewerController
